import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDramaById, getRelatedDramas, categories } from '@/mocks/dramas';
import NotFound from '@/pages/NotFound';

function Breadcrumb({ items }: { items: { label: string; href?: string }[] }) {
  return (
    <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
      {items.map((item, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <i className="ri-arrow-right-s-line text-xs"></i>}
          {item.href ? (
            <Link to={item.href} className="hover:text-primary-600 transition-colors whitespace-nowrap">
              {item.label}
            </Link>
          ) : (
            <span className="text-foreground-800 font-medium whitespace-nowrap">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

export default function DramaDetail() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const drama = getDramaById(id || '');

  if (!drama) {
    return <NotFound />;
  }

  const relatedDramas = getRelatedDramas(drama.id, drama.categories);
  const mainCategory = categories.find((c) => c.slug === drama.categories[0]);

  return (
    <main className="min-h-screen bg-background-50">
      <div className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src={drama.bannerImage}
          alt={drama.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-black/40"></div>
      </div>

      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 -mt-32 md:-mt-40 relative z-10">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: `${mainCategory?.name || '全部'}剧`, href: `/category/${drama.categories[0]}` },
            { label: drama.title },
          ]}
        />

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="flex-shrink-0 w-full sm:w-56 lg:w-64 mx-auto lg:mx-0">
            <div className="aspect-[3/4] rounded-xl overflow-hidden shadow-sm">
              <img
                src={drama.coverImage}
                alt={drama.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {drama.title}
              </h1>
              {drama.status === '更新中' && (
                <span className="bg-accent-500 text-background-50 text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap animate-pulse">
                  更新中
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="flex items-center gap-1 text-sm text-secondary-600 bg-secondary-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                <i className="ri-star-fill text-xs"></i>
                {drama.rating} 分
              </span>
              <span className="text-sm text-foreground-500 whitespace-nowrap">{drama.year}</span>
              <span className="text-sm text-foreground-500 whitespace-nowrap">{drama.episodes}集</span>
              <span className="text-sm text-foreground-500 whitespace-nowrap">{drama.region}</span>
              <span className="text-sm text-foreground-500 whitespace-nowrap">{drama.language}</span>
            </div>

            <div className="flex flex-wrap gap-2 mb-5">
              {drama.categories.map((catSlug) => {
                const cat = categories.find((c) => c.slug === catSlug);
                return cat ? (
                  <Link
                    key={catSlug}
                    to={`/category/${catSlug}`}
                    className="text-xs bg-background-100 text-foreground-600 px-3 py-1.5 rounded-full hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </Link>
                ) : null;
              })}
            </div>

            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
              {drama.longDescription}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-foreground-500 whitespace-nowrap">导演：</span>
                <span className="text-foreground-800 font-medium">{drama.director}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-foreground-500 whitespace-nowrap">主演：</span>
                <span className="text-foreground-800 font-medium">{drama.actors.join('、')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-foreground-500 whitespace-nowrap">更新状态：</span>
                <span className="text-foreground-800 font-medium">{drama.updateInfo}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-foreground-500 whitespace-nowrap">播放量：</span>
                <span className="text-foreground-800 font-medium">{(drama.views / 10000).toFixed(0)}万次</span>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-all hover:scale-105 cursor-pointer whitespace-nowrap">
                <i className="ri-play-circle-line mr-2"></i>
                立即观看
              </button>
              <button className="px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium rounded-full transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-heart-line mr-2"></i>
                收藏
              </button>
            </div>
          </div>
        </div>

        {relatedDramas.length > 0 && (
          <section className="mt-16 pb-14">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                相关推荐
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 stagger-children">
              {relatedDramas.map((related) => (
                <Link
                  key={related.id}
                  to={`/drama/${related.id}`}
                  className="group cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                    <img
                      src={related.coverImage}
                      alt={related.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                      <span className="text-white text-xs font-medium bg-primary-500/90 px-2 py-1 rounded whitespace-nowrap">
                        {related.rating} 分
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {related.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* JSON-LD Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: `${drama.title} - 桔子影视`,
              description: drama.description,
              author: {
                '@type': 'Organization',
                name: '桔子影视',
              },
              publisher: {
                '@type': 'Organization',
                name: '桔子影视',
              },
              datePublished: `${drama.year}-01-01`,
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': `https://juzitv.com/drama/${drama.id}`,
              },
            }),
          }}
        />
      </div>
    </main>
  );
}